<template>
  <!-- Wrap the canvas in a container with a fixed max-width -->
  <div class="radar-chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { onMounted, watch, ref } from 'vue'
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  name: 'RadarChart',
  props: {
    aptData: {
      type: Object,
      required: true
    },
    allAptGroups: {
      type: Array,
      default: null
    },
    baseAdvice: {
      type: Object,
      required: true
    },
    language: {
      type: String,
      default: 'en'
    }
  },
  emits: ['score-click'],
  setup(props, { emit }) {
    const chartCanvas = ref(null)
    let chartInstance = null

    // Parameter name translations
    const parameterTranslations = {
      en: {
        'Openness': 'Openness',
        'Conscientiousness': 'Conscientiousness',
        'Extraversion': 'Extraversion',
        'Agreeableness': 'Agreeableness',
        'Neuroticism': 'Neuroticism',
        'Machiavellianism': 'Machiavellianism',
        'Narcissism': 'Narcissism',
        'Psychopathy': 'Psychopathy',
        'Sadism': 'Sadism'
      },
      es: {
        'Openness': 'Apertura',
        'Conscientiousness': 'Responsabilidad',
        'Extraversion': 'Extraversión',
        'Agreeableness': 'Amabilidad',
        'Neuroticism': 'Neuroticismo',
        'Machiavellianism': 'Maquiavelismo',
        'Narcissism': 'Narcisismo',
        'Psychopathy': 'Psicopatía',
        'Sadism': 'Sádismo'
      }
    }

    const renderChart = () => {
      const ctx = chartCanvas.value.getContext('2d')
      
      // Determine if we're showing all APTs or just one
      const isShowingAll = props.allAptGroups && props.allAptGroups.length > 0
      
      // Get labels from the first APT (they should all have the same structure)
      const firstAPT = isShowingAll ? props.allAptGroups[0] : props.aptData
      const bigFiveLabels = Object.keys(firstAPT.bigFive)
      const darkTetradLabels = Object.keys(firstAPT.darkTetrad)
      const allLabels = [...bigFiveLabels, ...darkTetradLabels]
      
      // Translate labels based on current language
      const labels = allLabels.map(label => 
        parameterTranslations[props.language]?.[label] || label
      )
      
      // Generate datasets
      let datasets = []
      
      if (isShowingAll) {
        // Create a dataset for each APT group
        const colors = [
          '#DC780F', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
          '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE'
        ]
        
        props.allAptGroups.forEach((apt, index) => {
          const scores = [
            ...Object.values(apt.bigFive),
            ...Object.values(apt.darkTetrad)
          ]
          
          const color = colors[index % colors.length]
          datasets.push({
            label: apt.name,
            data: scores,
            fill: false,
            borderColor: color,
            borderWidth: 2,
            pointBackgroundColor: color,
            pointBorderColor: '#F7F3EE',
            pointBorderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: color,
            pointHoverBorderColor: '#F7F3EE'
          })
        })
      } else {
        // Single APT dataset
        const scores = [
          ...Object.values(props.aptData.bigFive),
          ...Object.values(props.aptData.darkTetrad)
        ]
        
        datasets.push({
          label: props.aptData.name,
          data: scores,
          fill: true,
          backgroundColor: 'rgba(220, 120, 15, 0.2)',
          borderColor: '#DC780F',
          borderWidth: 2,
          pointBackgroundColor: '#DC780F',
          pointBorderColor: '#F7F3EE',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#995400',
          pointHoverBorderColor: '#F7F3EE'
        })
      }

      if (chartInstance) {
        chartInstance.destroy()
      }
      chartInstance = new Chart(ctx, {
        type: 'radar',
        data: {
          labels,
          datasets
        },
        options: {
          responsive: true,
          plugins: {
                         tooltip: {
               backgroundColor: 'rgba(40, 40, 40, 0.95)',
               titleColor: '#F7F3EE',
               bodyColor: '#F7F3EE',
               borderColor: '#DC780F',
               borderWidth: 2,
               cornerRadius: 8,
               padding: 12,
               callbacks: {
                 title: (context) => {
                   const translatedTrait = context[0].label
                   const score = context[0].parsed.r
                   
                   // Check if multiple APTs have the same score for this trait
                   if (isShowingAll) {
                     const aptsWithSameScore = props.allAptGroups.filter(apt => {
                       const aptScores = [...Object.values(apt.bigFive), ...Object.values(apt.darkTetrad)]
                       // Find the index of the trait in the original labels array
                       const traitIndex = allLabels.findIndex((_, index) => 
                         parameterTranslations[props.language]?.[allLabels[index]] === translatedTrait
                       )
                       return traitIndex >= 0 && aptScores[traitIndex] === score
                     })
                     
                     if (aptsWithSameScore.length > 1) {
                       return `${translatedTrait} - Score: ${score}/5 (${aptsWithSameScore.length} APTs)`
                     }
                   }
                   
                   const datasetIndex = context[0].datasetIndex
                   const aptName = chartInstance.data.datasets[datasetIndex].label
                   return `${aptName} - ${translatedTrait}`
                 },
                 label: (context) => {
                    const translatedTrait = context.label
                    const score = context.parsed.r
                    const datasetIndex = context.datasetIndex
                    const aptName = chartInstance.data.datasets[datasetIndex].label
                    // Find the original trait name for data lookup
                    const originalTrait = allLabels.find((_, index) => 
                      parameterTranslations[props.language]?.[allLabels[index]] === translatedTrait
                    ) || translatedTrait
                    const advice = props.baseAdvice[originalTrait] && props.baseAdvice[originalTrait][score]
                    
                    // Check if multiple APTs have the same score for this trait
                    if (isShowingAll) {
                      const aptsWithSameScore = props.allAptGroups.filter(apt => {
                        const aptScores = [...Object.values(apt.bigFive), ...Object.values(apt.darkTetrad)]
                        // Find the index of the trait in the original labels array
                        const traitIndex = allLabels.findIndex((_, index) => 
                          parameterTranslations[props.language]?.[allLabels[index]] === translatedTrait
                        )
                        return traitIndex >= 0 && aptScores[traitIndex] === score
                      })
                      
                      if (aptsWithSameScore.length > 1) {
                        const aptNames = aptsWithSameScore.map(apt => apt.name).join(', ')
                        const result = [
                          `📊 Parameter: ${translatedTrait}`,
                          `🎯 Score: ${score}/5`,
                          `👥 APT Groups: ${aptNames}`
                        ]
                        
                        if (advice && typeof advice === 'object') {
                          result.push(`🔍 Indicator: ${advice.indicator || 'N/A'}`)
                          result.push(`🛡️ Defense: ${advice.defense || 'N/A'}`)
                        } else {
                          result.push(`ℹ️ No detailed advice available`)
                        }
                        
                        return result
                      }
                    }
                    
                    if (advice && typeof advice === 'object') {
                      return [
                        `📊 Parameter: ${translatedTrait}`,
                        `🎯 Score: ${score}/5`,
                        `🔍 Indicator: ${advice.indicator || 'N/A'}`,
                        `🛡️ Defense: ${advice.defense || 'N/A'}`
                      ]
                    } else {
                      return [
                        `📊 Parameter: ${translatedTrait}`,
                        `🎯 Score: ${score}/5`,
                        `ℹ️ No detailed advice available`
                      ]
                    }
                  }
               }
             },
            legend: {
              labels: {
                color: '#F7F3EE',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              }
            }
          },
                     onClick(e, elements) {
             if (elements.length > 0) {
               const element = elements[0]
               const idx = element.index
               const datasetIndex = element.datasetIndex
               const translatedTrait = chartInstance.data.labels[idx]
               const originalTrait = allLabels[idx] // Use original trait name for data lookup
               const score = chartInstance.data.datasets[datasetIndex].data[idx]
               const aptName = chartInstance.data.datasets[datasetIndex].label
               const advice = props.baseAdvice[originalTrait] && props.baseAdvice[originalTrait][score]
               
               // Emit detailed information including indicator and defense advice
               emit('score-click', { 
                 trait: translatedTrait, // Display translated name
                 originalTrait: originalTrait, // Keep original for reference
                 score,
                 aptName,
                 indicator: advice && typeof advice === 'object' ? advice.indicator : null,
                 defense: advice && typeof advice === 'object' ? advice.defense : null
               })
             }
           },
          scales: {
            r: {
              suggestedMin: 0,
              suggestedMax: 5,
              backgroundColor: '#5B5D62',
              grid: {
                color: 'rgba(220, 120, 15, 0.3)',
                lineWidth: 1
              },
              angleLines: {
                color: 'rgba(220, 120, 15, 0.3)',
                lineWidth: 1
              },
              pointLabels: {
                color: '#F7F3EE',
                font: {
                  size: 12,
                  weight: 'bold'
                }
              },
              ticks: {
                stepSize: 1,
                color: '#F7F3EE',
                font: {
                  size: 11
                },
                backdropColor: 'transparent'
              }
            }
          }
        }
      })
    }

    onMounted(() => {
      renderChart()
    })

    watch(() => props.aptData, () => {
      renderChart()
    })

    watch(() => props.language, () => {
      renderChart()
    })

    watch(() => props.allAptGroups, () => {
      renderChart()
    })

    return { chartCanvas }
  }
}
</script>

<style scoped>
.radar-chart-container {
  max-width: 600px;
  margin: auto;
  padding: 20px;
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--mm-custom-color-2);
}

.radar-chart-container canvas {
  border-radius: 10px;
}
</style>
